import { useEffect, useState } from 'react';
import { AcompanharAgente } from './components/AcompanharAgente';
import { Administracao } from './components/Administracao';
import { Benchessmark } from './components/Benchessmark';
import { EntradaNome } from './components/EntradaNome';
import { Inicio } from './components/Inicio';
import { Partida } from './components/Partida';
import { PreferenciasPainel } from './components/PreferenciasPainel';
import { PrepararPartida } from './components/PrepararPartida';
import { Sala } from './components/Sala';
import { listarModelosPublicos } from './lib/api';
import { listarNicknames, preferenciasDaSessao, removerConta, salvarPreferenciasNaConta } from './lib/contas';
import {
  apagarPartida,
  baixarSerieJson,
  baixarSeriePgn,
  carregarPartida,
  carregarPartidaAtiva,
  carregarPreferencias,
  definirSessaoAtiva,
  iniciarSerieArquivada,
  limparSessaoAtiva,
  listarPartidasArquivadas,
  obterSerieArquivada,
  obterSessaoAtiva,
  partidasDoJogador,
  PREFERENCIAS_PADRAO,
  registrarPartidaNaSerie,
  salvarPreferencias
} from './lib/storage';
import type {
  ConfiguracaoJogo,
  ModeloPublico,
  ModoJogo,
  PartidaOnlineEstado,
  PartidaPersistida,
  Preferencias,
  ResultadoPartidaInfo,
  SerieArquivada
} from './types';
import { nomeDoVencedor } from './lib/participantes';
import { CONTROLES_TEMPO } from './lib/controles';

type Tela = 'inicio' | 'preparar' | 'partida' | 'administracao' | 'benchessmark' | 'sala' | 'acompanhar-agente';

function tokenAcompanharDaRota(path: string): string | null {
  const m = path.match(/^\/acompanhar\/([A-Za-z0-9_-]{32,})$/);
  return m ? m[1] : null;
}

/**
 * Boot da app:
 * - Storage local = só este navegador/computador (nunca o do outro aparelho).
 * - Sessão ativa (localStorage) = última conta neste navegador (entra automático).
 * - Conta (nickname + senha) guarda preferências e histórico.
 * - Partida em andamento = por nickname neste navegador.
 */
function estadoInicialApp(): {
  tela: Tela;
  modo: ModoJogo;
  configuracao: ConfiguracaoJogo | null;
  restaurada: PartidaPersistida | null;
  partidaSalva: PartidaPersistida | null;
  precisaNome: boolean;
  tokenAgente: string | null;
} {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const sessaoBruta = obterSessaoAtiva();
  // Sessão só vale se ainda existir conta com esse nickname.
  const sessao = sessaoBruta && preferenciasDaSessao(sessaoBruta) ? sessaoBruta : null;
  if (sessaoBruta && !sessao) limparSessaoAtiva();
  const nome = sessao || '';
  const partidaDoJogador = nome ? carregarPartidaAtiva(nome) : null;
  const tokenAgente = tokenAcompanharDaRota(path);

  if (tokenAgente) {
    return {
      tela: 'acompanhar-agente',
      modo: 'local',
      configuracao: null,
      restaurada: null,
      partidaSalva: partidaDoJogador,
      precisaNome: false,
      tokenAgente
    };
  }
  if (path.startsWith('/administracao')) {
    return { tela: 'administracao', modo: 'local', configuracao: null, restaurada: null, partidaSalva: partidaDoJogador, precisaNome: !sessao, tokenAgente: null };
  }
  if (path.startsWith('/benchessmark')) {
    return { tela: 'benchessmark', modo: 'local', configuracao: null, restaurada: null, partidaSalva: partidaDoJogador, precisaNome: !sessao, tokenAgente: null };
  }
  // F5 / Ctrl+F5: volta ao salão. A partida em andamento fica disponível em "Retomar".
  return {
    tela: 'inicio',
    modo: 'local',
    configuracao: null,
    restaurada: null,
    partidaSalva: partidaDoJogador,
    precisaNome: !sessao,
    tokenAgente: null
  };
}

function ladoPorPapel(atual: ConfiguracaoJogo, papel: 'A' | 'B') {
  if (atual.brancasAuto?.papel === papel) return atual.brancasAuto;
  if (atual.pretasAuto?.papel === papel) return atual.pretasAuto;
  // Legado sem papel: A = quem batia com rotuloA / brancas iniciais
  if (papel === 'A') {
    if (atual.brancasAuto?.nome === atual.serie?.rotuloA) return atual.brancasAuto;
    if (atual.pretasAuto?.nome === atual.serie?.rotuloA) return atual.pretasAuto;
    return atual.brancasAuto;
  }
  if (atual.brancasAuto?.nome === atual.serie?.rotuloB) return atual.brancasAuto;
  if (atual.pretasAuto?.nome === atual.serie?.rotuloB) return atual.pretasAuto;
  return atual.pretasAuto;
}

function papelVencedorNaSerie(atual: ConfiguracaoJogo, vencedor: 'w' | 'b' | null): 'A' | 'B' | 'empate' {
  if (vencedor === null) return 'empate';
  const aNasBrancas =
    atual.brancasAuto?.papel === 'A' ||
    (!atual.brancasAuto?.papel && atual.brancasAuto?.nome === atual.serie?.rotuloA);
  if (vencedor === 'w') return aNasBrancas ? 'A' : 'B';
  return aNasBrancas ? 'B' : 'A';
}

function proximaConfiguracaoSerie(atual: ConfiguracaoJogo, info: ResultadoPartidaInfo): ConfiguracaoJogo | null {
  if (!atual.serie || !atual.brancasAuto || !atual.pretasAuto) return null;
  const s = atual.serie;
  let pontosA = s.pontosA;
  let pontosB = s.pontosB;
  let empates = s.empates;

  const aNasBrancas = atual.brancasAuto.papel === 'A' || (!atual.brancasAuto.papel && atual.brancasAuto.nome === s.rotuloA);
  if (info.vencedor === null) empates += 1;
  else if (info.vencedor === 'w') {
    if (aNasBrancas) pontosA += 1;
    else pontosB += 1;
  } else {
    if (aNasBrancas) pontosB += 1;
    else pontosA += 1;
  }

  // Grava a partida no arquivo da série (localStorage + exportável).
  if (s.idSerie) {
    const quem = nomeDoVencedor(atual, info.vencedor);
    registrarPartidaNaSerie(
      s.idSerie,
      {
        indice: s.indice,
        salvaEm: Date.now(),
        vencedor: info.vencedor,
        vencedorPapel: papelVencedorNaSerie(atual, info.vencedor),
        nomeVencedor: info.vencedor === null ? 'Empate' : quem || 'Desconhecido',
        motivo: info.motivo,
        pgn: info.pgn,
        lances: info.lances,
        nomeBrancas: atual.nomeBrancas,
        nomePretas: atual.nomePretas,
        pontosAApos: pontosA,
        pontosBApos: pontosB,
        empatesApos: empates
      },
      { pontosA, pontosB, empates, concluida: Boolean(info.encerrarSerie) || s.indice + 1 >= s.total }
    );
  }

  const proximoIndice = s.indice + 1;
  const serie = { ...s, indice: proximoIndice, pontosA, pontosB, empates };
  if (info.encerrarSerie || proximoIndice >= s.total) {
    return { ...atual, serie };
  }

  const findA = ladoPorPapel(atual, 'A') ?? atual.brancasAuto;
  const findB = ladoPorPapel(atual, 'B') ?? atual.pretasAuto;

  const inverter = s.inverterACadaPartida && proximoIndice % 2 === 1;
  const brancas = inverter ? findB : findA;
  const pretas = inverter ? findA : findB;

  return {
    ...atual,
    brancasAuto: brancas,
    pretasAuto: pretas,
    nomeBrancas: brancas.nome,
    nomePretas: pretas.nome,
    serie
  };
}

export function App() {
  const [boot] = useState(() => estadoInicialApp());
  const [preferencias, setPreferencias] = useState<Preferencias>(() => {
    const base = carregarPreferencias();
    const sessao = obterSessaoAtiva();
    if (sessao) {
      const daConta = preferenciasDaSessao(sessao);
      if (daConta) return { ...daConta, nomesSalvos: listarNicknames() };
    }
    return { ...base, nomeJogador: '', nomesSalvos: listarNicknames() };
  });
  const [partidaSalva, setPartidaSalva] = useState<PartidaPersistida | null>(() => boot.partidaSalva);
  const [tela, setTela] = useState<Tela>(() => boot.tela);
  const [modo, setModo] = useState<ModoJogo>(() => boot.modo);
  const [configuracao, setConfiguracao] = useState<ConfiguracaoJogo | null>(() => boot.configuracao);
  const [restaurada, setRestaurada] = useState<PartidaPersistida | null>(() => boot.restaurada);
  const [modelos, setModelos] = useState<ModeloPublico[]>([]);
  const [preferenciasAbertas, setPreferenciasAbertas] = useState(false);
  const [chavePartida, setChavePartida] = useState(0);
  const [resumoSerie, setResumoSerie] = useState<string | null>(null);
  const [serieFinal, setSerieFinal] = useState<SerieArquivada | null>(null);
  const [precisaNome, setPrecisaNome] = useState(() => boot.precisaNome);
  const [nicknameLogin, setNicknameLogin] = useState('');
  const [tokenAgente, setTokenAgente] = useState<string | null>(() => boot.tokenAgente);

  useEffect(() => {
    document.documentElement.dataset.tema = preferencias.tema;
    document.documentElement.dataset.tabuleiro = preferencias.corTabuleiro ?? 'nogueira';
    document.documentElement.classList.toggle('movimento-reduzido', preferencias.reduzirMovimento);
    const comLista = { ...preferencias, nomesSalvos: listarNicknames() };
    salvarPreferencias(comLista);
    if (preferencias.nomeJogador?.trim()) {
      salvarPreferenciasNaConta(preferencias.nomeJogador, preferencias);
    }
  }, [preferencias]);
  useEffect(() => {
    void listarModelosPublicos()
      .then(setModelos)
      .catch(() => setModelos([]));
  }, [tela]);
  useEffect(() => {
    const aoVoltar = () => {
      const token = tokenAcompanharDaRota(window.location.pathname);
      if (token) {
        setTokenAgente(token);
        setTela('acompanhar-agente');
        return;
      }
      setTokenAgente(null);
      if (window.location.pathname.startsWith('/administracao')) setTela('administracao');
      else if (window.location.pathname.startsWith('/benchessmark')) setTela('benchessmark');
      else if (window.location.pathname.startsWith('/sala')) setTela('sala');
      else setTela('inicio');
    };
    window.addEventListener('popstate', aoVoltar);
    return () => window.removeEventListener('popstate', aoVoltar);
  }, []);

  function navegar(nova: Tela) {
    setTela(nova);
    const caminho =
      nova === 'administracao' ? '/administracao'
      : nova === 'benchessmark' ? '/benchessmark'
      : nova === 'sala' ? '/sala'
      : '/';
    if (window.location.pathname !== caminho) window.history.pushState({}, '', caminho);
  }

  function aoEntrarConta(prefs: Preferencias) {
    const nome = prefs.nomeJogador?.trim() || '';
    setPreferencias({ ...prefs, nomesSalvos: listarNicknames() });
    if (nome) definirSessaoAtiva(nome);
    setPrecisaNome(false);
    setNicknameLogin('');
    setPartidaSalva(nome ? carregarPartidaAtiva(nome) : null);
    setRestaurada(null);
    setConfiguracao(null);
    setTela('inicio');
  }

  /** Abre o login pedindo senha de outra conta (não troca sem autenticação). */
  function pedirLoginConta(nickname?: string) {
    limparSessaoAtiva();
    setNicknameLogin(nickname?.trim() || '');
    setPrecisaNome(true);
  }

  function listaNomesAposRemover(removido: string): string[] {
    const lower = removido.trim().toLowerCase();
    const contas = listarNicknames();
    const legado = (preferencias.nomesSalvos ?? []).filter(
      (n) => n.trim().toLowerCase() !== lower && !contas.some((c) => c.toLowerCase() === n.trim().toLowerCase())
    );
    return [...contas, ...legado].sort((a, b) => a.localeCompare(b, 'pt'));
  }

  /** Remove conta/nome deste aparelho (e partida em andamento desse nickname). */
  function removerNomeDoAparelho(nickname: string) {
    const nome = nickname.trim();
    if (!nome) return;
    if (
      !window.confirm(
        `Remover “${nome}” deste aparelho?\n\nApaga a conta, a senha, as preferências e a partida em andamento desse nome.`
      )
    ) {
      return;
    }
    removerConta(nome);
    apagarPartida(nome);
    const lista = listaNomesAposRemover(nome);
    const eraAtual =
      preferencias.nomeJogador?.trim().toLowerCase() === nome.toLowerCase() ||
      obterSessaoAtiva()?.toLowerCase() === nome.toLowerCase();
    if (eraAtual) {
      limparSessaoAtiva();
      setPreferencias({ ...PREFERENCIAS_PADRAO, nomesSalvos: lista, nomeJogador: '' });
      setPartidaSalva(null);
      setConfiguracao(null);
      setRestaurada(null);
      setPrecisaNome(true);
      setNicknameLogin('');
      setTela('inicio');
      return;
    }
    setPreferencias((p) => ({ ...p, nomesSalvos: lista }));
  }

  function descartarPartidaSalva() {
    if (!window.confirm('Descartar a partida em andamento neste aparelho?')) return;
    apagarPartida(preferencias.nomeJogador);
    setPartidaSalva(null);
    setRestaurada(null);
    setConfiguracao(null);
  }

  function iniciarOnline(partida: PartidaOnlineEstado, clienteId: string, apenasAssistir = false) {
    const souBrancas = !apenasAssistir && partida.brancasId === clienteId;
    const souPretas = !apenasAssistir && partida.pretasId === clienteId;
    const corJogador: 'w' | 'b' = souBrancas ? 'w' : souPretas ? 'b' : 'w';
    const controle = CONTROLES_TEMPO.find((c) => c.id === '10-0') ?? CONTROLES_TEMPO[0];
    if (!apenasAssistir) {
      apagarPartida(preferencias.nomeJogador);
      setPartidaSalva(null);
    }
    const config = {
      modo: 'online' as const,
      corJogador,
      controle,
      nivel: '1600' as const,
      estilo: 'equilibrado' as const,
      onlinePartidaId: partida.id,
      onlineClienteId: clienteId,
      permitirDesfazer: false,
      inverterAutomaticamente: false,
      nomeBrancas: partida.nomeBrancas,
      nomePretas: partida.nomePretas,
      apenasAssistir: Boolean(apenasAssistir),
      /** Espelha se a partida aceita espectadores (para UI). */
      onlinePermitirEspectadores: Boolean(partida.permitirEspectadores)
    };
    setRestaurada({
      versao: 1,
      idPartida: partida.id,
      configuracao: config,
      pgn: partida.pgn || '',
      fen: partida.fen,
      orientacao: corJogador,
      tempoBrancas: controle.minutos * 60_000,
      tempoPretas: controle.minutos * 60_000,
      iniciada: true,
      ativa: true,
      salvaEm: Date.now()
    });
    setConfiguracao(config);
    setChavePartida((k) => k + 1);
    setTela('partida');
  }
  function abrirModo(novoModo: ModoJogo) {
    if (novoModo === 'online') {
      navegar('sala');
      return;
    }
    setModo(novoModo);
    setRestaurada(null);
    setResumoSerie(null);
    setSerieFinal(null);
    setTela('preparar');
  }
  function iniciar(nova: ConfiguracaoJogo) {
    apagarPartida(preferencias.nomeJogador);
    setPartidaSalva(null);
    // Lembra dificuldade e cronômetro na conta do jogador.
    setPreferencias((atual) => ({
      ...atual,
      nivelPreferido: nova.nivel,
      controlePreferido: nova.controle.id,
      minutosPersonalizados: nova.controle.id === 'personalizado' ? nova.controle.minutos : atual.minutosPersonalizados,
      incrementoPersonalizado: nova.controle.id === 'personalizado' ? nova.controle.incremento : atual.incrementoPersonalizado
    }));
    // Abre registro da série no storage (duelo = 1 jogo; benchmark = N).
    if (nova.serie?.idSerie) {
      iniciarSerieArquivada({
        id: nova.serie.idSerie,
        modo: nova.modo,
        rotuloA: nova.serie.rotuloA,
        rotuloB: nova.serie.rotuloB,
        total: nova.serie.total,
        controle: nova.controle.nome,
        inverterACadaPartida: nova.serie.inverterACadaPartida
      });
    }
    setConfiguracao(nova);
    setRestaurada(null);
    setResumoSerie(null);
    setSerieFinal(null);
    setChavePartida((k) => k + 1);
    setTela('partida');
  }
  function retomar() {
    const salva = carregarPartida(preferencias.nomeJogador);
    if (!salva) {
      setPartidaSalva(null);
      return;
    }
    setConfiguracao(salva.configuracao);
    setRestaurada(salva);
    setChavePartida((k) => k + 1);
    setTela('partida');
  }
  function voltarInicio() {
    const salva = carregarPartida(preferencias.nomeJogador);
    setPartidaSalva(salva?.ativa ? salva : null);
    setConfiguracao(null);
    setRestaurada(null);
    navegar('inicio');
  }
  function novaPartida() {
    apagarPartida(preferencias.nomeJogador);
    setPartidaSalva(null);
    setRestaurada(null);
    setConfiguracao(null);
    setTela('preparar');
  }

  function aoConcluirPartida(info: ResultadoPartidaInfo) {
    if (!configuracao?.serie) return;
    const proxima = proximaConfiguracaoSerie(configuracao, info);
    if (!proxima?.serie) return;
    const serieFechada = Boolean(info.encerrarSerie) || proxima.serie.indice >= proxima.serie.total;
    if (serieFechada) {
      const s = proxima.serie;
      const arquivada = s.idSerie ? obterSerieArquivada(s.idSerie) : null;
      const jogadas = arquivada?.partidas.length ?? Math.min(s.indice, s.total);
      const primeira = arquivada?.partidas[0];
      const textoPrimeira = primeira
        ? ` 1ª partida: ${primeira.nomeVencedor} (${primeira.motivo}).`
        : '';
      setResumoSerie(
        info.encerrarSerie
          ? `Benchmark interrompido: ${s.rotuloA} ${s.pontosA} × ${s.pontosB} ${s.rotuloB} (${s.empates} empates) após ${jogadas} partida${jogadas === 1 ? '' : 's'}.${textoPrimeira}`
          : `Série encerrada: ${s.rotuloA} ${s.pontosA} × ${s.pontosB} ${s.rotuloB} (${s.empates} empates) em ${s.total} partidas.${textoPrimeira}`
      );
      setSerieFinal(arquivada);
      setConfiguracao(proxima);
      // Resultado fica gravado; download só sob demanda (botões do modal).
      return;
    }
    // Próxima partida da série
    window.setTimeout(() => {
      apagarPartida(preferencias.nomeJogador);
      setRestaurada(null);
      setConfiguracao(proxima);
      setChavePartida((k) => k + 1);
    }, 900);
  }

  // Duelo avulso (série de 1) também precisa registrar ao terminar.
  function aoConcluirQualquer(info: ResultadoPartidaInfo) {
    if (configuracao?.serie) {
      aoConcluirPartida(info);
      return;
    }
  }

  const historicoNome = preferencias.nomeJogador?.trim()
    ? partidasDoJogador(preferencias.nomeJogador)
    : listarPartidasArquivadas().slice(0, 20);

  return (
    <>
      {tela === 'acompanhar-agente' && tokenAgente && (
        <AcompanharAgente
          token={tokenAgente}
          aoSair={() => {
            setTokenAgente(null);
            navegar('inicio');
          }}
        />
      )}
      {precisaNome && tela !== 'acompanhar-agente' && (
        <EntradaNome
          preferencias={preferencias}
          nicknameInicial={nicknameLogin}
          aoEntrar={aoEntrarConta}
          aoRemoverNome={removerNomeDoAparelho}
        />
      )}
      {tela === 'inicio' && !precisaNome && (
        <Inicio
          preferencias={preferencias}
          existePartida={Boolean(partidaSalva)}
          historico={historicoNome}
          aoEscolherModo={abrirModo}
          aoRetomar={retomar}
          aoDescartarPartida={descartarPartidaSalva}
          aoAbrirAdministracao={() => navegar('administracao')}
          aoAbrirPreferencias={() => setPreferenciasAbertas(true)}
          aoAlternarSom={() => setPreferencias({ ...preferencias, somAtivo: !preferencias.somAtivo })}
          aoAbrirSala={() => navegar('sala')}
          aoTrocarNome={() => pedirLoginConta()}
          aoEscolherNome={(n) => pedirLoginConta(n)}
          aoRemoverNome={removerNomeDoAparelho}
        />
      )}
      {tela === 'sala' && preferencias.nomeJogador && (
        <Sala
          nomeJogador={preferencias.nomeJogador}
          aoVoltar={() => navegar('inicio')}
          aoIniciarOnline={iniciarOnline}
        />
      )}
      {tela === 'preparar' && (
        <PrepararPartida
          modo={modo}
          modelos={modelos}
          preferencias={preferencias}
          aoVoltar={() => setTela('inicio')}
          aoIniciar={iniciar}
          aoAdministrar={() => navegar('administracao')}
          nomeJogador={preferencias.nomeJogador}
        />
      )}
      {tela === 'partida' && configuracao && (
        <>
          {resumoSerie && (
            <div className="sobreposicao resultado-sobreposicao" style={{ zIndex: 110 }}>
              <section className="modal-resultado" role="dialog" aria-modal="true">
                <small>
                  {serieFinal && serieFinal.partidas.length < serieFinal.total
                    ? 'BENCHMARK INTERROMPIDO · GRAVADO'
                    : 'BENCHMARK CONCLUÍDO · GRAVADO'}
                </small>
                <h2>Placar final</h2>
                <p>{resumoSerie}</p>
                <p className="dica-download-serie">
                  O resultado foi salvo neste navegador. Use os botões abaixo se quiser baixar o arquivo.
                </p>
                {serieFinal && serieFinal.partidas.length > 0 && (
                  <div className="resumo-serie-partidas">
                    {serieFinal.partidas.map((p) => (
                      <div key={p.indice} className="linha-resumo-serie">
                        <strong>#{p.indice + 1}</strong>
                        <span>{p.nomeVencedor}</span>
                        <small>{p.motivo} · {p.lances} lances</small>
                      </div>
                    ))}
                  </div>
                )}
                <div className="acoes-resultado">
                  {serieFinal && (
                    <>
                      <button type="button" className="botao-principal" onClick={() => baixarSerieJson(serieFinal)}>
                        Baixar resultado (JSON)
                      </button>
                      <button type="button" className="botao-contorno" onClick={() => baixarSeriePgn(serieFinal)}>
                        Baixar PGN
                      </button>
                    </>
                  )}
                  <button type="button" className="botao-contorno" onClick={novaPartida}>
                    Nova série
                  </button>
                  <button type="button" className="botao-contorno" onClick={voltarInicio}>
                    Voltar ao salão
                  </button>
                </div>
              </section>
            </div>
          )}
          <Partida
            key={chavePartida}
            configuracao={configuracao}
            preferencias={preferencias}
            restaurada={restaurada}
            aoAlterarPreferencias={setPreferencias}
            aoSair={voltarInicio}
            aoNovaPartida={novaPartida}
            aoConcluir={configuracao.serie ? aoConcluirPartida : aoConcluirQualquer}
          />
        </>
      )}
      {tela === 'benchessmark' && (
        <Benchessmark
          aoVoltar={() => navegar('inicio')}
          aoAdministrar={() => navegar('administracao')}
        />
      )}
      {tela === 'administracao' && (
        <Administracao
          aoVoltar={() => {
            void listarModelosPublicos()
              .then(setModelos)
              .catch(() => setModelos([]));
            navegar('inicio');
          }}
          aoAbrirLab={(modoLab) => {
            setModo(modoLab);
            setRestaurada(null);
            setResumoSerie(null);
            setSerieFinal(null);
            setTela('preparar');
          }}
          aoAbrirBenchessmark={() => navegar('benchessmark')}
        />
      )}
      {preferenciasAbertas && (
        <PreferenciasPainel preferencias={preferencias} aoAlterar={setPreferencias} aoFechar={() => setPreferenciasAbertas(false)} />
      )}
    </>
  );
}
